import { ChevronLeft, ChevronRight, Settings } from 'lucide-react';
import { addDays, addMonths, addWeeks, format } from 'date-fns';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import type { CalendarViewMode } from './calendar-config';
import { cn } from '@/lib/utils';

// ============================================
// CALENDAR TOOLBAR
// ============================================
//
// t16: small action row above the active view. Prev / Next / Today on
// the left, the current title in the middle, and the view-mode
// selector + Settings popover trigger on the right. The Settings
// popover lives in calendar-settings-panel.tsx and is rendered by
// CalendarView (the parent) so it can own the onConfigChange callback.
//
// T21 R4: the Settings button is now <Popover.Trigger asChild> — it
// opens the panel that CalendarView wraps via <Popover.Root>. The
// previous "forward a ref to the button so the panel could read
// button.getBoundingClientRect() via Popover.Anchor virtualRef" hack
// was unreliable (Radix Popper reads its reference element from the
// PopperContext set by Trigger/Anchor, not from a Content prop) and
// silently fell back to off-screen positioning when the timing was
// wrong. Radix's natural Trigger/Content pattern just works.
//
// T30 R6: responsive toolbar — on phone (≤640px) the toolbar stacks
// into two rows: nav + title on top, view-mode tabs + cog below.
// Everything keeps its own min-width so a 375px viewport (iPhone)
// still shows every control without horizontal overflow.

export interface CalendarToolbarProps {
    anchor: Date;
    viewMode: CalendarViewMode;
    onAnchorChange: (next: Date) => void;
    onViewModeChange: (mode: CalendarViewMode) => void;
}

function shiftAnchor(anchor: Date, viewMode: CalendarViewMode, direction: 1 | -1): Date {
    switch (viewMode) {
        case 'week':
            return addWeeks(anchor, direction);
        case 'agenda':
            return addDays(anchor, 14 * direction);
        case 'month':
        default:
            return addMonths(anchor, direction);
    }
}

function anchorTitle(anchor: Date, viewMode: CalendarViewMode, t: (k: any) => string): string {
    if (viewMode === 'agenda') {
        return `${format(anchor, 'MMM d')} — ${format(addDays(anchor, 14), 'MMM d, yyyy')}`;
    }
    if (viewMode === 'week') {
        return `${format(anchor, 'MMM d')} — ${format(addDays(anchor, 6), 'MMM d, yyyy')}`;
    }
    return format(anchor, 'MMMM yyyy');
}

export function CalendarToolbar({
    anchor,
    viewMode,
    onAnchorChange,
    onViewModeChange,
}: CalendarToolbarProps) {
    const { t } = useI18n();

    const title = anchorTitle(anchor, viewMode, t);

    return (
        // T30 R6: flex-col on mobile stacks the two halves (nav row +
        // tabs row). sm:flex-row brings them back into a single row
        // on tablet+.
        <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
            {/* Nav + title row. */}
            <div className="flex items-center gap-1 min-w-0">
                <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => onAnchorChange(shiftAnchor(anchor, viewMode, -1))}
                    aria-label={t('calendar.previous')}
                >
                    <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0"
                    onClick={() => onAnchorChange(new Date())}
                >
                    {t('common.today')}
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => onAnchorChange(shiftAnchor(anchor, viewMode, 1))}
                    aria-label={t('calendar.next')}
                >
                    <ChevronRight className="h-4 w-4" />
                </Button>
                {/* Title: shrinks to fit, hides margin on mobile. */}
                <span className="ml-1 sm:ml-2 text-sm font-medium text-foreground capitalize truncate min-w-0">
                    {title}
                </span>
            </div>

            {/* Tabs + cog row. Stacks on mobile too (gap-2 handles
                spacing) but gets justify-end on sm+ so it pins right. */}
            <div className="flex items-center gap-2 justify-between sm:justify-end">
                <div
                    role="tablist"
                    aria-label={t('calendar.viewModeLabel')}
                    className="inline-flex items-center rounded-md border border-white/10 bg-white/5 p-0.5"
                >
                    {(['month', 'week', 'agenda'] as CalendarViewMode[]).map((mode) => (
                        <button
                            key={mode}
                            type="button"
                            role="tab"
                            aria-selected={viewMode === mode}
                            data-state={viewMode === mode ? 'active' : 'inactive'}
                            onClick={() => onViewModeChange(mode)}
                            className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors cursor-pointer select-none',
                                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/50',
                                viewMode === mode
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-foreground-muted hover:text-foreground hover:bg-white/5'
                            )}
                        >
                            {t(`calendar.viewMode.${mode}`)}
                        </button>
                    ))}
                </div>
                {/* T21 R4: Radix Trigger owns the click → open transition
                    and implicitly registers itself as the anchor for the
                    panel's Popover.Content. */}
                <Popover.Trigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        // T16 R3: slightly bigger and clearer so the user can
                        // find the cog button at a glance. The outline variant
                        // is fine but a subtle background + border tint makes it
                        // pop on dark themes.
                        className="h-9 w-9 shrink-0 cursor-pointer bg-white/5 border-white/15 hover:bg-white/10 hover:border-white/25"
                        aria-label={t('calendar.settings')}
                        title={t('calendar.settings')}
                        data-testid="calendar-settings-button"
                    >
                        <Settings className="h-4 w-4" />
                    </Button>
                </Popover.Trigger>
            </div>
        </div>
    );
}